import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { InventoryListFilterValue, InventoryListFilterView } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { useIntl } from "react-intl"
import { createTagDismissHandler } from "src/common/createTagDismissHandler"
import { mapArrayValues, mapCategorizedArrayValues, mapSingleValue } from "src/common/filterTagValueMappers"
import { getFeatureToggle } from "src/common/getConfig"
import { TagList } from "src/components"
import { inventoryListFilterMessages } from "../inventoryListFilters/inventoryListFilter.messages"

const listingQualityLabelMap = {
	GOOD_QUALITY: inventoryListFilterMessages.goodQuality,
	LOW_QUALITY: inventoryListFilterMessages.lowQuality,
	MEDIUM_QUALITY: inventoryListFilterMessages.mediumQuality,
} as const

interface InventoryActiveFiltersTagListsProps {
	className?: string
	filter: InventoryListFilterView | undefined
	onChange: (value: InventoryListFilterValue, key: keyof InventoryListFilterValue, action?: "tag-dismissed") => void
	value: InventoryListFilterValue
}

export const InventoryActiveFiltersTagLists = (props: InventoryActiveFiltersTagListsProps) => {
	const { filter, onChange, value } = props
	const { formatMessage, formatNumber } = useIntl()
	const { currency, distance } = useDealerContext()
	const isSmartSearchEnabled = getFeatureToggle("smart-search")

	const tagListValues = useMemo(
		() => ({
			accidentFree: value.accidentFree ? [formatMessage(inventoryListFilterMessages.accidentFree)] : undefined,
			bodyTypes: mapArrayValues(value.bodyTypes, filter?.bodyTypes),
			dealershipIds: mapArrayValues(value.dealershipIds, filter?.dealershipIds),
			derivatives: mapCategorizedArrayValues(value.derivatives, filter?.derivatives),
			driveTrains: mapArrayValues(value.driveTrains, filter?.driveTrains),
			engineTypes: mapArrayValues(value.engineTypes, filter?.engineTypes),
			equipmentTags: mapCategorizedArrayValues(value.equipmentTags, filter?.equipmentTags),
			exteriorColors: mapArrayValues(value.exteriorColors, filter?.exteriorColors),
			interiorColors: mapArrayValues(value.interiorColors, filter?.interiorColors),
			listingQuality: value.listingQuality?.map((quality) => formatMessage(listingQualityLabelMap[quality])),
			listingVisibility: mapArrayValues(value.listingVisibility, filter?.listingVisibility),
			maxFirstRegistrationDate: mapSingleValue(value.maxFirstRegistrationDate, filter?.maxFirstRegistrationDate),
			maxMileage: value.maxMileage
				? [`${formatNumber(Number.parseInt(value.maxMileage), { style: "decimal" })} ${distance}`]
				: undefined,
			maxModelYear: mapSingleValue(value.maxModelYear, filter?.maxModelYear),
			maxPreviousOwners: value.previousOwners
				? [`${formatNumber(Number.parseInt(value.previousOwners), { style: "decimal" })}`]
				: undefined,
			maxPrice: value.maxPrice
				? [`${formatNumber(Number.parseInt(value.maxPrice), { currency, style: "currency" })}`]
				: undefined,
			minEnginePower: value.minEnginePower
				? [
						`${formatNumber(Number.parseInt(value.minEnginePower), { style: "decimal" })} ${value.enginePowerUnit ?? "kW"}`,
					]
				: undefined,
			minFirstRegistrationDate: mapSingleValue(value.minFirstRegistrationDate, filter?.minFirstRegistrationDate),
			minModelYear: mapSingleValue(value.minModelYear, filter?.minModelYear),
			modelGenerations: mapArrayValues(value.modelGenerations, filter?.modelGenerations),
			modelSeries: Array.isArray(value.modelSeries)
				? mapArrayValues(value.modelSeries, filter?.modelSeries)
				: mapSingleValue(value.modelSeries, filter?.modelSeries),
			onlineOrderableStatus: mapArrayValues(value.onlineOrderableStatus, filter?.onlineOrderableStatus),
			salesStatus: mapArrayValues(value.salesStatus, filter?.salesStatus),
			search: value.search ? [value.search] : undefined,
			stockStatus: mapArrayValues(value.stockStatus, filter?.stockStatus),
			transmissions: mapArrayValues(value.transmissions, filter?.transmissions),
			vatDeductible: value.vatDeductible ? [formatMessage(inventoryListFilterMessages.vatDeductible)] : undefined,
			vehicleTypes: mapArrayValues(value.vehicleTypes, filter?.vehicleTypes),
		}),
		[filter, value, currency, distance, formatNumber, formatMessage],
	)

	const onTagDismiss = createTagDismissHandler(value, onChange)

	const activeFiltersCount = useMemo(
		() => Object.values(tagListValues).filter((v) => v !== undefined && Array.isArray(v) && v.length > 0).length,
		[tagListValues],
	)

	return (
		filter &&
		activeFiltersCount > 0 && (
			<>
				{isSmartSearchEnabled && tagListValues.search && tagListValues.search.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.search)}
						values={tagListValues.search}
						onDismiss={onTagDismiss("search")}
					/>
				)}
				{tagListValues.accidentFree && tagListValues.accidentFree.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.condition)}
						values={tagListValues.accidentFree}
						onDismiss={onTagDismiss("accidentFree")}
					/>
				)}
				{tagListValues.listingQuality && tagListValues.listingQuality.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.listingQuality)}
						values={tagListValues.listingQuality}
						onDismiss={onTagDismiss("listingQuality")}
					/>
				)}
				{tagListValues.vehicleTypes && tagListValues.vehicleTypes.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.condition)}
						values={tagListValues.vehicleTypes}
						onDismiss={onTagDismiss("vehicleTypes")}
					/>
				)}
				{tagListValues.modelSeries && tagListValues.modelSeries.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.modelSeries)}
						values={tagListValues.modelSeries}
						onDismiss={onTagDismiss("modelSeries")}
					/>
				)}
				{tagListValues.derivatives && tagListValues.derivatives.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.modelVariants)}
						values={tagListValues.derivatives}
						onDismiss={onTagDismiss("derivatives")}
					/>
				)}
				{tagListValues.modelGenerations && tagListValues.modelGenerations.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.modelGenerations)}
						values={tagListValues.modelGenerations}
						onDismiss={onTagDismiss("modelGenerations")}
					/>
				)}
				{tagListValues.bodyTypes && tagListValues.bodyTypes.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.bodyType)}
						values={tagListValues.bodyTypes}
						onDismiss={onTagDismiss("bodyTypes")}
					/>
				)}
				{tagListValues.driveTrains && tagListValues.driveTrains.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.driveTrains)}
						values={tagListValues.driveTrains}
						onDismiss={onTagDismiss("driveTrains")}
					/>
				)}
				{tagListValues.engineTypes && tagListValues.engineTypes.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.engineTypes)}
						values={tagListValues.engineTypes}
						onDismiss={onTagDismiss("engineTypes")}
					/>
				)}
				{tagListValues.transmissions && tagListValues.transmissions.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.transmissions)}
						values={tagListValues.transmissions}
						onDismiss={onTagDismiss("transmissions")}
					/>
				)}
				{tagListValues.equipmentTags && tagListValues.equipmentTags.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.equipment)}
						values={tagListValues.equipmentTags}
						onDismiss={onTagDismiss("equipmentTags")}
					/>
				)}
				{tagListValues.exteriorColors && tagListValues.exteriorColors.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.exteriorColor)}
						values={tagListValues.exteriorColors}
						onDismiss={onTagDismiss("exteriorColors")}
					/>
				)}
				{tagListValues.interiorColors && tagListValues.interiorColors.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.interiorColor)}
						values={tagListValues.interiorColors}
						onDismiss={onTagDismiss("interiorColors")}
					/>
				)}
				{tagListValues.maxPrice && tagListValues.maxPrice.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.maxPrice)}
						values={tagListValues.maxPrice}
						onDismiss={onTagDismiss("maxPrice")}
					/>
				)}
				{tagListValues.maxPreviousOwners && tagListValues.maxPreviousOwners.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.maxPreviousOwners)}
						values={tagListValues.maxPreviousOwners}
						onDismiss={onTagDismiss("previousOwners")}
					/>
				)}
				{tagListValues.minEnginePower && tagListValues.minEnginePower.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.minEnginePower)}
						values={tagListValues.minEnginePower}
						onDismiss={onTagDismiss("minEnginePower")}
					/>
				)}
				{tagListValues.maxMileage && tagListValues.maxMileage.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.maxMileage)}
						values={tagListValues.maxMileage}
						onDismiss={onTagDismiss("maxMileage")}
					/>
				)}
				{tagListValues.minModelYear && tagListValues.minModelYear.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.minModelYear)}
						values={tagListValues.minModelYear}
						onDismiss={onTagDismiss("minModelYear")}
					/>
				)}
				{tagListValues.maxModelYear && tagListValues.maxModelYear.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.maxModelYear)}
						values={tagListValues.maxModelYear}
						onDismiss={onTagDismiss("maxModelYear")}
					/>
				)}
				{tagListValues.minFirstRegistrationDate && tagListValues.minFirstRegistrationDate.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.minFirstRegistrationDate)}
						values={tagListValues.minFirstRegistrationDate}
						onDismiss={onTagDismiss("minFirstRegistrationDate")}
					/>
				)}
				{tagListValues.maxFirstRegistrationDate && tagListValues.maxFirstRegistrationDate.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.maxFirstRegistrationDate)}
						values={tagListValues.maxFirstRegistrationDate}
						onDismiss={onTagDismiss("maxFirstRegistrationDate")}
					/>
				)}
				{tagListValues.salesStatus && tagListValues.salesStatus.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.salesStatus)}
						values={tagListValues.salesStatus}
						onDismiss={onTagDismiss("salesStatus")}
					/>
				)}
				{tagListValues.stockStatus && tagListValues.stockStatus.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.stockStatus)}
						values={tagListValues.stockStatus}
						onDismiss={onTagDismiss("stockStatus")}
					/>
				)}
				{tagListValues.vatDeductible && tagListValues.vatDeductible.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.price)}
						values={tagListValues.vatDeductible}
						onDismiss={onTagDismiss("vatDeductible")}
					/>
				)}
				{tagListValues.onlineOrderableStatus && tagListValues.onlineOrderableStatus.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.onlineOrderableStatus)}
						values={tagListValues.onlineOrderableStatus}
						onDismiss={onTagDismiss("onlineOrderableStatus")}
					/>
				)}
				{tagListValues.listingVisibility && tagListValues.listingVisibility.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.listingVisibility)}
						values={tagListValues.listingVisibility}
						onDismiss={onTagDismiss("listingVisibility")}
					/>
				)}
				{tagListValues.dealershipIds && tagListValues.dealershipIds.length > 0 && (
					<TagList
						label={formatMessage(inventoryListFilterMessages.location)}
						values={tagListValues.dealershipIds}
						onDismiss={onTagDismiss("dealershipIds")}
					/>
				)}
			</>
		)
	)
}
